// src/components/EscalationQueue.jsx
//
// The working queue for CRITICAL escalations. Shared by the doctor Dashboard
// page (mode="doctor") and the navigator Dashboard (mode="navigator") so the two
// roles see one list with different actions, not two look-alikes to keep in sync.
//
// WHO DOES WHAT
// -------------
//   Doctor    decides: acknowledge, resolve, snooze, hand an urgent/critical
//             escalation to a navigator, and SUGGEST a specialist.
//   Navigator does the outreach: message the patient, book the specialist the
//             doctor suggested (via the normal booking flow) and mark it booked,
//             acknowledge, and resolve once the patient is dealt with.
//
// A doctor never books; the specialist request lands in the patient's
// navigator's queue.
//
// Endpoints: see doctorEscalationAPI in services/doctorApi.js. Doctors only ever
// receive their own patients' rows — the server enforces that, not this file.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { matchesQuery } from '../utils/searchMatch';
import { displayPatientCode } from '../utils/patientCode';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { doctorEscalationAPI } from '../services/doctorApi';
import {
  SLA_HOURS, isUnacknowledged, isSnoozed, isSlaBreached, doctorTab,
  needsSpecialistBooking,
} from '../utils/escalationState';

// Older than this and an unresolved escalation is offered for clean-up.
const STALE_DAYS = 7;

const RESOLUTION_OUTCOMES = [
  { value: 'REVIEWED_NO_ACTION', label: 'Clinically reviewed – no action needed' },
  { value: 'TREATMENT_CHANGED',  label: 'Treatment changed' },
  { value: 'REFERRED',           label: 'Referred / ER advised' },
  { value: 'PATIENT_CONTACTED',  label: 'Patient contacted' },
  { value: 'FALSE_POSITIVE',     label: 'False positive / data error' },
  { value: 'STALE_NOT_RELEVANT', label: 'Old – no longer clinically relevant' },
];

const OUTCOME_LABEL = Object.fromEntries(RESOLUTION_OUTCOMES.map(o => [o.value, o.label]));

const SPECIALTIES = [
  { value: 'CARDIOLOGY',       label: 'Cardiology' },
  { value: 'NEPHROLOGY',       label: 'Nephrology' },
  { value: 'ENDOCRINOLOGY',    label: 'Endocrinology' },
  { value: 'ONCOLOGY',         label: 'Oncology' },
  { value: 'GYNAECOLOGY',      label: 'Gynaecology' },
  { value: 'GASTROENTEROLOGY', label: 'Gastroenterology' },
  { value: 'NEUROLOGY',        label: 'Neurology' },
  { value: 'HEMATOLOGY',       label: 'Haematology' },
  { value: 'OTHER',            label: 'Other (describe in note)' },
];

const SPECIALTY_LABEL = Object.fromEntries(SPECIALTIES.map(s => [s.value, s.label]));

const SNOOZE_CHOICES = [
  { hours: 1,  label: '1 hour' },
  { hours: 2,  label: '2 hours' },
  { hours: 4,  label: '4 hours' },
  { hours: 8,  label: '8 hours' },
  { hours: 24, label: '1 day' },
  { hours: 48, label: '2 days' },
  { hours: 72, label: '3 days' },
];

// ── Small helpers ────────────────────────────────────────────────────────────

function errMessage(err) {
  return err?.response?.data?.message
    || (typeof err?.response?.data === 'string' ? err.response.data : null)
    || err?.message
    || 'Something went wrong. Please try again.';
}

function duration(ms) {
  const mins = Math.max(0, Math.round(ms / 60000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function formatWhen(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

// The escalation payload carries patientName; if it is missing (patient row
// gone, or a backend that predates the field) fall back to the patient's code
// (PRV-7K3M9Q) — what staff read out and search by. Never the patient id: that
// is an internal key, not something to show as if it identified the patient.
function patientLabel(patientCode, patientName) {
  if (patientName && patientName.trim()) return patientName;
  return displayPatientCode(patientCode) || 'Unknown patient';
}

const ageMs = e => Date.now() - new Date(e.createdAt).getTime();
const isStale = e => e.status !== 'RESOLVED' && ageMs(e) > STALE_DAYS * 86_400_000;

// CRITICAL first, then longest-waiting first — the one that has waited longest
// and matters most is at the top, not whichever landed last.
function byUrgency(a, b) {
  const pa = a.priority === 'CRITICAL' ? 0 : 1;
  const pb = b.priority === 'CRITICAL' ? 0 : 1;
  if (pa !== pb) return pa - pb;
  return new Date(a.createdAt) - new Date(b.createdAt);
}

const byResolvedDesc = (a, b) => new Date(b.resolvedAt) - new Date(a.resolvedAt);

// ── Dialog ───────────────────────────────────────────────────────────────────

function Dialog({ title, sub, onClose, children }) {
  useEffect(() => {
    const onKey = ev => { if (ev.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Portalled to <body>: the queue sits inside a `.card.fade-in-up`, whose
  // animation leaves `transform: translateY(0)` behind. Any transform makes
  // the card the containing block for `position: fixed`, so rendered in place
  // the backdrop covered only the card and the dialog was centred on the card —
  // off-screen when the list was long.
  return createPortal(
    <div className="dialog-backdrop" onClick={onClose}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={ev => ev.stopPropagation()}
      >
        <div className="dialog-title">{title}</div>
        {sub && <div className="dialog-sub">{sub}</div>}
        {children}
      </div>
    </div>,
    document.body,
  );
}

// One form for every action that needs input. `kind` picks the fields;
// `onSubmit(payload)` does the call and throws on failure.
function ActionDialog({ kind, count, defaultOutcome, onSubmit, onClose }) {
  const [outcome, setOutcome]     = useState(defaultOutcome || 'REVIEWED_NO_ACTION');
  const [specialty, setSpecialty] = useState('CARDIOLOGY');
  const [hours, setHours]         = useState(4);
  const [note, setNote]           = useState('');
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState(null);

  const many = count > 1;
  const noun = many ? `${count} escalations` : 'this escalation';

  const META = {
    resolve: {
      title: 'Resolve escalation',
      sub: `Close ${noun} and record why.`,
      submit: 'Resolve',
    },
    assign: {
      title: 'Assign to navigator',
      sub: `The patient's own navigator will get ${noun} to contact the patient. `
        + 'You can add instructions below.',
      submit: 'Assign',
    },
    snooze: {
      title: 'Snooze escalation',
      sub: 'It will come back as a fresh alert when the time is up.',
      submit: 'Snooze',
    },
    specialist: {
      title: 'Suggest a specialist',
      sub: 'You suggest; the navigator books the appointment and confirms it here.',
      submit: 'Suggest',
    },
    booked: {
      title: 'Mark specialist booked',
      sub: 'Confirm the suggested specialist appointment has been booked.',
      submit: 'Mark booked',
    },
    acknowledge: {
      title: 'Acknowledge escalation',
      sub: `Confirm you have seen ${noun}.`,
      submit: 'Acknowledge',
    },
  }[kind];

  const noteRequired =
    (kind === 'resolve' && outcome === 'FALSE_POSITIVE')
    || (kind === 'snooze')
    || (kind === 'specialist' && specialty === 'OTHER');

  const noteLabel = {
    resolve: 'Note',
    assign: 'Instructions for the navigator',
    snooze: 'Reason',
    specialist: 'Note for the navigator',
    booked: 'Appointment details',
    acknowledge: 'Note',
  }[kind];

  async function submit(ev) {
    ev.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const trimmed = note.trim();
      if (kind === 'resolve')          await onSubmit({ outcome, note: trimmed });
      else if (kind === 'snooze')      await onSubmit({ hours: Number(hours), reason: trimmed });
      else if (kind === 'specialist')  await onSubmit({ specialty, note: trimmed });
      else                             await onSubmit({ note: trimmed });
    } catch (err) {
      setError(errMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog title={META.title} sub={META.sub} onClose={busy ? () => {} : onClose}>
      <form onSubmit={submit}>
        {error && <div className="alert alert-error" role="alert">{error}</div>}

        {kind === 'resolve' && (
          <div className="form-group">
            <label className="form-label" htmlFor="esc-outcome">Outcome</label>
            <select
              id="esc-outcome"
              className="form-select"
              value={outcome}
              onChange={ev => setOutcome(ev.target.value)}
            >
              {RESOLUTION_OUTCOMES.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        )}

        {kind === 'snooze' && (
          <div className="form-group">
            <label className="form-label" htmlFor="esc-hours">Snooze for</label>
            <select
              id="esc-hours"
              className="form-select"
              value={hours}
              onChange={ev => setHours(ev.target.value)}
            >
              {SNOOZE_CHOICES.map(c => (
                <option key={c.hours} value={c.hours}>{c.label}</option>
              ))}
            </select>
          </div>
        )}

        {kind === 'specialist' && (
          <div className="form-group">
            <label className="form-label" htmlFor="esc-specialty">Specialty</label>
            <select
              id="esc-specialty"
              className="form-select"
              value={specialty}
              onChange={ev => setSpecialty(ev.target.value)}
            >
              {SPECIALTIES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group">
          <label className="form-label" htmlFor="esc-note">
            {noteLabel}
            {noteRequired && <span className="required">*</span>}
          </label>
          <textarea
            id="esc-note"
            className="form-textarea"
            rows={3}
            maxLength={500}
            value={note}
            required={noteRequired}
            onChange={ev => setNote(ev.target.value)}
          />
        </div>

        <div className="dialog-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : META.submit}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// ── One escalation ───────────────────────────────────────────────────────────

function statusBadge(e) {
  if (e.status === 'RESOLVED') {
    return <span className="badge badge-active">Resolved</span>;
  }
  if (isSnoozed(e)) {
    return <span className="badge badge-unknown">Snoozed until {formatWhen(e.snoozedUntil)}</span>;
  }
  if (e.status === 'ACKNOWLEDGED') {
    return <span className="badge badge-confirmed">Acknowledged</span>;
  }
  if (isSlaBreached(e)) {
    const over = e.slaDueAt ? duration(Date.now() - new Date(e.slaDueAt).getTime()) : null;
    return (
      <span className="badge badge-critical">
        Overdue{over ? ` ${over}` : ''} · {SLA_HOURS}h SLA
      </span>
    );
  }
  if (e.slaDueAt) {
    const left = duration(new Date(e.slaDueAt).getTime() - Date.now());
    return <span className="badge badge-pending">SLA in {left}</span>;
  }
  return <span className="badge badge-pending">Notifying…</span>;
}

const ROLE_LABEL = {
  DOCTOR: 'doctor', HEALTH_NAVIGATOR: 'navigator', ADMIN: 'admin', SUPPORT_STAFF: 'support staff',
};

// "Deepu Nair (navigator)" — doctors already come as "Dr. Saju John".
function whoLabel(name, role) {
  if (!name) return null;
  const r = ROLE_LABEL[role];
  return r && role !== 'DOCTOR' ? `${name} (${r})` : name;
}

function Item({
  e, mode, showPatient, selected, onToggleSelect, expanded, onToggleExpand, onAction, onOpen,
}) {
  const navigate = useNavigate();
  const critical = e.priority === 'CRITICAL';
  const resolved = e.status === 'RESOLVED';
  const isDoctor = mode === 'doctor';
  const suggested = e.suggestedSpecialty;
  const booked = !!e.specialistBookedAt;

  const patientBase = isDoctor ? '/doctor/patients' : '/patients';

  return (
    <div className={`esc-item${critical ? ' esc-item--critical' : ''}`}>
      <div
        className="esc-head"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggleExpand}
        onKeyDown={ev => {
          if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onToggleExpand(); }
        }}
      >
        {!resolved && (
          <input
            type="checkbox"
            className="esc-check"
            aria-label="Select escalation"
            checked={selected}
            onClick={ev => ev.stopPropagation()}
            onChange={onToggleSelect}
          />
        )}
        <div className="esc-main">
          {showPatient && <div className="esc-patient">{patientLabel(e.patientCode, e.patientName)}</div>}
          <div className="esc-text">
            {e.acuteAlertDetail || `Risk level ${e.riskLevel || 'HIGH'}`}
          </div>
          <div className="esc-meta">
            <span className={`badge ${critical ? 'badge-critical' : 'badge-high'}`}>
              {critical ? 'Critical' : 'High'}
            </span>
            {statusBadge(e)}
            {e.status === 'ACKNOWLEDGED' && !isSnoozed(e) && (
              <>
                {whoLabel(e.acknowledgedByName, e.acknowledgedByRole) && (
                  <span>by <strong>{whoLabel(e.acknowledgedByName, e.acknowledgedByRole)}</strong></span>
                )}
                {!e.doctorReviewedAt && (
                  <span className="badge badge-pending">Awaiting doctor review</span>
                )}
              </>
            )}
            {!resolved && e.hasDoctor === false && (
              <span
                className="badge badge-pending"
                title="This patient has no doctor. Only on-call doctors can see this escalation.">
                No doctor assigned
              </span>
            )}
            {/* Without a doctor's handoff, name the patient's own navigator.
                That is a label only: the escalation is still unassigned in the
                doctor's workflow (the assign button and tabs ignore it). */}
            {!resolved && (
              e.assignedNavigatorId
                ? <span>Navigator: <strong>{e.assignedNavigatorName || 'Assigned'}</strong></span>
                : e.patientNavigatorId
                  ? (e.patientNavigatorName
                    ? <span>Navigator: <strong>{e.patientNavigatorName}</strong></span>
                    : null)
                  : <span>Unassigned</span>
            )}
            {suggested && (
              <span className={`badge ${booked ? 'badge-active' : 'badge-purple'}`}>
                {SPECIALTY_LABEL[suggested] || suggested} {booked ? 'booked' : 'suggested'}
              </span>
            )}
            {resolved && e.resolutionOutcome && (
              <span>{OUTCOME_LABEL[e.resolutionOutcome] || e.resolutionOutcome}</span>
            )}
          </div>
        </div>
        <span className="esc-when">
          {formatWhen(resolved ? e.resolvedAt : (e.notifiedAt || e.createdAt))}
        </span>
      </div>

      {expanded && (
        <div className="esc-body">
          <div className="esc-text">{e.acuteAlertDetail || `Risk level ${e.riskLevel || 'HIGH'}`}</div>

          {e.acknowledgedAt && (
            <div className="esc-note">
              <strong>Acknowledged</strong>
              {whoLabel(e.acknowledgedByName, e.acknowledgedByRole)
                ? <> by {whoLabel(e.acknowledgedByName, e.acknowledgedByRole)}</> : null}
              {' '}on {formatWhen(e.acknowledgedAt)}
              {e.acknowledgementNote ? <> — {e.acknowledgementNote}</> : null}
            </div>
          )}
          {e.doctorReviewedAt && e.doctorReviewedBy !== e.acknowledgedBy && (
            <div className="esc-note">
              <strong>Reviewed</strong>
              {e.doctorReviewedByName ? <> by {e.doctorReviewedByName}</> : null}
              {' '}on {formatWhen(e.doctorReviewedAt)}
            </div>
          )}
          {e.assignmentNote && (
            <div className="esc-note"><strong>Instructions:</strong> {e.assignmentNote}</div>
          )}
          {suggested && e.specialistNote && (
            <div className="esc-note"><strong>Specialist note:</strong> {e.specialistNote}</div>
          )}
          {e.specialistBookingNote && (
            <div className="esc-note"><strong>Booking:</strong> {e.specialistBookingNote}</div>
          )}
          {isSnoozed(e) && e.snoozeReason && (
            <div className="esc-note"><strong>Snoozed because:</strong> {e.snoozeReason}</div>
          )}
          {resolved && e.resolutionNote && (
            <div className="esc-note"><strong>Resolution note:</strong> {e.resolutionNote}</div>
          )}

          <div className="esc-actions">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => (onOpen ? onOpen(e) : navigate(`${patientBase}/${e.patientId}`))}
              disabled={!e.patientId}
            >
              Open patient
            </button>

            {!resolved && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => navigate(`${patientBase}/${e.patientId}/chat`)}
                disabled={!e.patientId}
              >
                Message patient
              </button>
            )}

            {!resolved && (isDoctor ? !e.doctorReviewedAt : isUnacknowledged(e)) && (
              <button type="button" className="btn btn-secondary btn-sm"
                onClick={() => onAction('acknowledge', [e])}>
                {isUnacknowledged(e) ? 'Acknowledge' : 'Mark reviewed'}
              </button>
            )}

            {!resolved && isDoctor && (
              <>
                <button type="button" className="btn btn-primary btn-sm"
                  onClick={() => onAction('assign', [e])}>
                  {e.assignedNavigatorId ? 'Reassign navigator' : 'Assign to navigator'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => onAction('specialist', [e])}>
                  {suggested ? 'Change specialist' : 'Suggest specialist'}
                </button>
                <button type="button" className="btn btn-ghost btn-sm"
                  onClick={() => onAction('snooze', [e])}>
                  Snooze
                </button>
              </>
            )}

            {!resolved && !isDoctor && needsSpecialistBooking(e) && (
              <>
                <button type="button" className="btn btn-primary btn-sm"
                  onClick={() => navigate('/appointments/new')}>
                  Book appointment
                </button>
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => onAction('booked', [e])}>
                  Mark booked
                </button>
              </>
            )}

            {!resolved && (
              <button type="button" className="btn btn-secondary btn-sm"
                onClick={() => onAction('resolve', [e])}>
                Resolve
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── The queue ────────────────────────────────────────────────────────────────

export default function EscalationQueue({ mode = 'doctor', onChanged, query = '', initialTab, overdueOnly: initialOverdueOnly = false }) {
  const isDoctor = mode === 'doctor';

  const [lists, setLists]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [tab, setTab]                 = useState(initialTab || (isDoctor ? 'open' : 'mine'));
  const [overdueOnly, setOverdueOnly] = useState(initialOverdueOnly);
  const [priority, setPriority]       = useState('ALL');
  const [patientFilter, setPatientFilter] = useState('ALL');   // 'ALL' | patientId
  const [staleOnly, setStaleOnly]     = useState(false);
  const [selected, setSelected]       = useState(() => new Set());
  const [expanded, setExpanded]       = useState(() => new Set());
  const [dialog, setDialog]           = useState(null);   // { kind, items }
  const [notice, setNotice]           = useState(null);   // { tone, text }

  const load = useCallback(async () => {
    setError(null);
    try {
      if (isDoctor) {
        const [active, resolved] = await Promise.all([
          doctorEscalationAPI.list({ view: 'ACTIVE' }),
          doctorEscalationAPI.list({ view: 'RESOLVED' }),
        ]);
        // Filter on status as well as trusting `view`: a backend that predates
        // the view filter answers every request with the open queue, which
        // would otherwise show the same rows under Resolved.
        setLists({
          active: (active.data || []).filter(e => e.status !== 'RESOLVED'),
          resolved: (resolved.data || []).filter(e => e.status === 'RESOLVED'),
        });
      } else {
        const [active, mine, resolved] = await Promise.all([
          doctorEscalationAPI.list({ view: 'ACTIVE' }),
          doctorEscalationAPI.list({ view: 'ACTIVE', assignedToMe: true }),
          doctorEscalationAPI.list({ view: 'RESOLVED', assignedToMe: true }),
        ]);
        setLists({
          active: (active.data || []).filter(e => e.status !== 'RESOLVED'),
          mine: (mine.data || []).filter(e => e.status !== 'RESOLVED'),
          resolved: (resolved.data || []).filter(e => e.status === 'RESOLVED'),
        });
      }
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }, [isDoctor]);

  useEffect(() => { load(); }, [load]);

  const tabs = useMemo(() => {
    if (!lists) return [];
    if (isDoctor) {
      const a = lists.active;
      const inTab = key => a.filter(e => doctorTab(e) === key);
      return [
        { key: 'open',      label: 'Needs response',  items: inTab('open') },
        { key: 'navigator', label: 'With navigator',  items: inTab('navigator') },
        { key: 'progress',  label: 'In progress',     items: inTab('progress') },
        { key: 'snoozed',   label: 'Snoozed',         items: inTab('snoozed') },
        { key: 'resolved',  label: 'Resolved',        items: lists.resolved },
      ];
    }
    return [
      { key: 'mine',     label: 'Assigned to me', items: lists.mine.filter(e => !isSnoozed(e)) },
      { key: 'open',     label: 'Needs response', items: lists.active.filter(isUnacknowledged) },
      { key: 'progress', label: 'In progress',    items: lists.active.filter(e => e.status === 'ACKNOWLEDGED' && !isSnoozed(e)) },
      { key: 'resolved', label: 'Resolved',       items: lists.resolved },
    ];
  }, [lists, isDoctor]);

  const current = tabs.find(t => t.key === tab) || tabs[0];
  const isResolvedTab = current?.key === 'resolved';

  // Every patient with an escalation in any tab, so a chosen patient stays
  // selectable when switching tabs. `count` is that patient's rows in the tab
  // being viewed, which is what the number next to the name should mean.
  const patientOptions = useMemo(() => {
    const byId = new Map();
    tabs.forEach(t => t.items.forEach(e => {
      if (e.patientId && !byId.has(e.patientId)) {
        byId.set(e.patientId, { id: e.patientId, label: patientLabel(e.patientCode, e.patientName) });
      }
    }));
    const counts = new Map();
    (current?.items || []).forEach(e => counts.set(e.patientId, (counts.get(e.patientId) || 0) + 1));
    return [...byId.values()]
      .map(o => ({ ...o, count: counts.get(o.id) || 0 }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tabs, current]);

  // A refresh can drop the chosen patient from every list; fall back to "all"
  // rather than filtering to a value the dropdown can no longer show.
  const activePatient = patientOptions.some(o => o.id === patientFilter) ? patientFilter : 'ALL';

  const patientItems = useMemo(
    () => (!current ? [] : activePatient === 'ALL'
      ? current.items
      : current.items.filter(e => e.patientId === activePatient)),
    [current, activePatient],
  );

  const staleCount = useMemo(
    () => (current && !isResolvedTab ? patientItems.filter(isStale).length : 0),
    [current, isResolvedTab, patientItems],
  );

  const visible = useMemo(() => {
    if (!current) return [];
    let items = patientItems;
    if (priority !== 'ALL') items = items.filter(e => e.priority === priority);
    if (staleOnly && !isResolvedTab) items = items.filter(isStale);
    if (overdueOnly && !isResolvedTab) items = items.filter(isSlaBreached);
    if (query.trim()) {
      items = items.filter(e => matchesQuery(
        query, patientLabel(e.patientCode, e.patientName), e.acuteAlertDetail, e.riskLevel,
        e.acknowledgedByName, e.assignedNavigatorName, e.patientNavigatorName,
        SPECIALTY_LABEL[e.suggestedSpecialty] || e.suggestedSpecialty,
      ));
    }
    return [...items].sort(isResolvedTab ? byResolvedDesc : byUrgency);
  }, [current, patientItems, priority, staleOnly, overdueOnly, isResolvedTab, query]);

  // Drop selections that are no longer on screen (tab switch, filter, refresh)
  // so a bulk action can never touch a row the user cannot see.
  useEffect(() => {
    const ids = new Set(visible.map(e => e.id));
    setSelected(prev => {
      const next = new Set([...prev].filter(id => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visible]);

  const selectedItems = visible.filter(e => selected.has(e.id));

  function toggle(setter, id) {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleMany(items) {
    setSelected(prev => {
      const next = new Set(prev);
      const allOn = items.every(e => next.has(e.id));
      items.forEach(e => (allOn ? next.delete(e.id) : next.add(e.id)));
      return next;
    });
  }

  function openDialog(kind, items) {
    setNotice(null);
    setDialog({ kind, items });
  }

  async function afterChange(message) {
    setDialog(null);
    setSelected(new Set());
    if (message) setNotice({ tone: 'success', text: message });
    await load();
    if (onChanged) onChanged();
  }

  // Runs the dialog's action for one or many escalations.
  async function submitDialog(payload) {
    const { kind, items } = dialog;
    const ids = items.map(e => e.id);

    if (items.length > 1 || kind === 'acknowledge') {
      const action = { resolve: 'RESOLVE', assign: 'ASSIGN_NAVIGATOR', acknowledge: 'ACKNOWLEDGE' }[kind];
      if (action) {
        const { data } = await doctorEscalationAPI.bulk({
          ids, action, outcome: payload.outcome, note: payload.note,
        });
        const failed = data.failed || [];
        setDialog(null);
        setSelected(new Set());
        setNotice(failed.length
          ? { tone: 'warning', text: `${data.succeeded.length} done, ${failed.length} failed: ${failed[0].reason}` }
          : { tone: 'success', text: `${data.succeeded.length} escalation${data.succeeded.length === 1 ? '' : 's'} updated.` });
        await load();
        if (onChanged) onChanged();
        return;
      }
    }

    const id = ids[0];
    if (kind === 'resolve')         await doctorEscalationAPI.resolve(id, payload);
    else if (kind === 'assign')     await doctorEscalationAPI.assignToNavigator(id, { note: payload.note });
    else if (kind === 'snooze')     await doctorEscalationAPI.snooze(id, payload);
    else if (kind === 'specialist') await doctorEscalationAPI.suggestSpecialist(id, payload);
    else if (kind === 'booked')     await doctorEscalationAPI.markSpecialistBooked(id, { note: payload.note });
    await afterChange('Saved.');
  }

  if (loading) {
    return (
      <div className="skeleton-list">
        {[...Array(3)].map((_, i) => <div key={i} className="skeleton skeleton--row" />)}
      </div>
    );
  }

  if (error) {
    return (
      <>
        <div className="alert alert-error" role="alert">{error}</div>
        <button type="button" className="btn btn-ghost" onClick={() => { setLoading(true); load(); }}>
          Retry
        </button>
      </>
    );
  }

  const bulkResolveDefault = staleOnly ? 'STALE_NOT_RELEVANT' : undefined;
  // A doctor can act on anything no doctor has reviewed yet (including what a
  // navigator already acknowledged); a navigator only on what is unacknowledged.
  const ackable = selectedItems.filter(e => (isDoctor ? !e.doctorReviewedAt : isUnacknowledged(e)));
  const anyUnacknowledged = ackable.length > 0;

  return (
    <div>
      <div className="tabs-bar">
        {tabs.map(t => (
          <button
            key={t.key}
            type="button"
            className={`tab-btn${t.key === current.key ? ' active' : ''}`}
            onClick={() => { setTab(t.key); setStaleOnly(false); setOverdueOnly(false); setNotice(null); }}
          >
            {t.label} ({t.items.length})
          </button>
        ))}
      </div>

      <div className="esc-toolbar">
        <select
          className="form-select"
          aria-label="Filter by priority"
          value={priority}
          onChange={ev => setPriority(ev.target.value)}
        >
          <option value="ALL">All priorities</option>
          <option value="CRITICAL">Critical only</option>
          <option value="HIGH">High only</option>
        </select>
        <select
          className="form-select"
          aria-label="Filter by patient"
          value={activePatient}
          onChange={ev => setPatientFilter(ev.target.value)}
        >
          <option value="ALL">All patients</option>
          {patientOptions.map(o => (
            <option key={o.id} value={o.id}>{o.label} ({o.count})</option>
          ))}
        </select>
        <span className="esc-toolbar-spacer" />
        {!isResolvedTab && visible.length > 0 && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => toggleMany(visible)}>
            {visible.every(e => selected.has(e.id)) ? 'Clear selection' : 'Select all'}
          </button>
        )}
      </div>

      {notice && (
        <div className={`alert alert-${notice.tone}`} role="status">{notice.text}</div>
      )}

      {overdueOnly && !isResolvedTab && (
        <div className="esc-banner">
          <span>Showing only escalations past the {SLA_HOURS}h SLA.</span>
          <span className="esc-toolbar-spacer" />
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOverdueOnly(false)}>
            Show all
          </button>
        </div>
      )}

      {!isResolvedTab && staleCount > 0 && (
        <div className="esc-banner">
          <span>
            {staleCount} escalation{staleCount === 1 ? ' is' : 's are'} more than {STALE_DAYS} days old.
            Review and clear what is no longer relevant.
          </span>
          <span className="esc-toolbar-spacer" />
          <button type="button" className="btn btn-secondary btn-sm"
            onClick={() => setStaleOnly(s => !s)}>
            {staleOnly ? 'Show all' : 'Show only these'}
          </button>
        </div>
      )}

      {selectedItems.length > 0 && (
        <div className="esc-bulkbar" role="region" aria-label="Bulk actions">
          <span>{selectedItems.length} selected</span>
          <span className="esc-toolbar-spacer" />
          {anyUnacknowledged && (
            <button type="button" className="btn btn-secondary btn-sm"
              onClick={() => openDialog('acknowledge', ackable)}>
              Acknowledge
            </button>
          )}
          {isDoctor && (
            <button type="button" className="btn btn-primary btn-sm"
              onClick={() => openDialog('assign', selectedItems)}>
              Assign to navigator
            </button>
          )}
          <button type="button" className="btn btn-secondary btn-sm"
            onClick={() => openDialog('resolve', selectedItems)}>
            Resolve
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>
            Clear
          </button>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="empty-state" style={{ padding: '20px 0' }}>
          <div className="empty-desc">
            {current.items.length === 0
              ? (isResolvedTab ? 'Nothing resolved in the last 90 days.' : 'Nothing here.')
              : (query.trim() ? 'No escalations match your search.' : 'No escalations match these filters.')}
          </div>
        </div>
      ) : (
        <div className="esc-list">
          {visible.map(e => (
            <Item
              key={e.id}
              e={e}
              mode={mode}
              showPatient
              selected={selected.has(e.id)}
              onToggleSelect={() => toggle(setSelected, e.id)}
              expanded={expanded.has(e.id)}
              onToggleExpand={() => toggle(setExpanded, e.id)}
              onAction={openDialog}
            />
          ))}
        </div>
      )}

      {dialog && (
        <ActionDialog
          kind={dialog.kind}
          count={dialog.items.length}
          defaultOutcome={dialog.kind === 'resolve' ? bulkResolveDefault : undefined}
          onSubmit={submitDialog}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
